import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import ShipmentCusSidebarLayout from "../ShipmentSidebar/ShipmentCusSidebarLayout";
import ShipCusAdvanceFormLayout from "./ShipCusAdvanceSearch/ShipCusAdvanceFormLayout";
import ViewWoByCus from "./ViewWoByCus";

export default function CustomerTab({
  activeFilter,
  setActiveFilter,
  shipment,
  showCusAdvanceForm,
  setShowCusAdvanceForm,
  selectedCustomer,
  onCustomerSelect,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userInfo);

  const [customersList, setCustomersList] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerLimit, setCustomerLimit] = useState(10);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [customerPages, setCustomerPages] = useState(1);
  const [cusQuery, setCusQuery] = useState("");

  // Advance Form Fields
  const [isAdvanceMode, setIsAdvanceMode] = useState(false);
  const [requestList, setRequestList] = useState([
    {
      requestId: 1,
      // ContactAndBilling
      customerId: "",
      customerName: "",
      externalCustomerId: "",
      website: "",
      accountPayableContact: "",
      accountPayablePhone: "",
      accountPayableEmail: "",
      addressCompany: "",
      addressLine1: "",
      addressLine2: "",

      // CustomerDropDowns
      locationId: "",
      taxAccountId: "",
      paymentTermsId: "",
      salesPersonId: "",
      salesManager: "",
      poRequired: "",

      // CustomFields
      customField1: "",
      customField2: "",
      customField3: "",
    },
  ]);

  const resetRequestList = () => {
    setRequestList([
      {
        requestId: 1,
        // ContactAndBilling
        customerId: "",
        customerName: "",
        externalCustomerId: "",
        website: "",
        accountPayableContact: "",
        accountPayablePhone: "",
        accountPayableEmail: "",
        addressCompany: "",
        addressLine1: "",
        addressLine2: "",

        // CustomerDropDowns
        locationId: "",
        taxAccountId: "",
        paymentTermsId: "",
        salesPersonId: "",
        salesManager: "",
        poRequired: "",

        // CustomFields
        customField1: "",
        customField2: "",
        customField3: "",
      },
    ]);
  };

  // Status Base Counts
  const [filterTabCounts, setFilterTabCounts] = useState({
    processing: 0,
    // "p&p": 0,
    pending: 0,
    all: 0,
  });

  // Local debounced search input to update cusQuery (prop) and trigger fetch
  const [showWOModal, setShowWOModals] = useState(false);

  const navItems = [
    {
      key: "processing",
      label: "Processing",
      icon: "mdi-progress-clock",
    },
    {
      key: "pending",
      label: "Pending",
      icon: "mdi-clock-outline",
    },
    // {
    //   key: "p&p",
    //   label: "P & P",
    //   icon: "mdi-clock-outline",
    // },
    {
      key: "all",
      label: "All Customers",
      icon: "mdi-clipboard-text",
    },
  ];

  /*   All Functions
   ********************************************* */

  const loadingOn = () => {
    setCustomerLoading(true);
  };
  const loadingOff = () => {
    setCustomerLoading(false);
  };

  const getCustomersOfPendingProcessingOrders = useCallback(
    async (opts = {}) => {
      try {
        setCustomerLoading(true);
        loadingOn();
        const pageToUse = opts.page || customerPage;
        let body = {
          page: pageToUse,
          limit: opts?.limit || customerLimit,
          query: cusQuery,
          shipmentId: shipment?.shipmentId,
          searchFields: ["customerName"],
        };
        if (activeFilter !== "all") {
          body.WOStatus = activeFilter;
        }
        const { data } = await API.post(
          "/sales/shipment/customers-list/p-p-wo-orders",
          body,
          { headers: authHeader() }
        );
        if (data?.success) {
          if (pageToUse === 1) {
            setCustomersList(data?.data || []);
          } else {
            setCustomersList((prev) => [...prev, ...(data?.data || [])]);
          }
          setFilterTabCounts({
            processing: data?.totalProcessingCount,
            pending: data?.totalPendingCount,
            all: data?.allCustomersCount,
            // "p&p": data?.totalPendingProcessingCount,
          });
          setTotalCustomers(data?.total || 0);
          setCustomerPage(data?.page || pageToUse);
          setCustomerLimit(data?.limit || customerLimit);
          setCustomerPages(data?.pages || 1);
        }
        loadingOff();
        setCustomerLoading(false);
      } catch (err) {
        loadingOff();
        setCustomerLoading(false);
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      }
    },
    [customerLimit, cusQuery, activeFilter, dispatch, customerPage]
  );

  const handleLoadMoreCustomers = async () => {
    if (isAdvanceMode) {
      await AdvanceSearch({ page: customerPage + 1 });
    } else {
      await getCustomersOfPendingProcessingOrders({ page: customerPage + 1 });
    }
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customer/details/${customerId}`);
  };

  // Advance Search Form
  const AdvanceSearch = async (opts = {}) => {
    try {
      /**
        WOStatus,
      requestList,
      shipmentId,
      page = 1,
      limit = 10,
      customerId = null,
      query = null,
      searchFields = [],
       */

      const pageToUse = opts.page || customerPage;
      let body = {
        page: pageToUse,
        limit: opts?.limit || customerLimit,
        query: cusQuery,
        shipmentId: shipment?.shipmentId,
        searchFields: ["customerName"],
      };

      setCustomerLoading(true);
      const { data } = await API.post(
        "/sales/shipment/customer/advance-search",
        {
          ...body,
          WOStatus: activeFilter,
          requestList,
          shipmentId: shipment?.shipmentId,
        },
        {
          headers: authHeader(),
        }
      );

      if (data?.success) {
        if (pageToUse === 1) {
          setCustomersList(data?.data || []);
        } else {
          setCustomersList((prev) => [...prev, ...(data?.data || [])]);
        }
        setFilterTabCounts({
          processing: data?.totalProcessingCount,
          pending: data?.totalPendingCount,
          all: data?.allCustomersCount,
          // "p&p": data?.totalPendingProcessingCount,
        });
        setTotalCustomers(data?.total || 0);
        setCustomerPage(data?.page || pageToUse);
        setCustomerLimit(data?.limit || customerLimit);
        setCustomerPages(data?.pages || 1);
      }

      setCustomerLoading(false);
    } catch (err) {
      setCustomerLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    const t = setTimeout(() => {
      if (isAdvanceMode === false) {
        setCusQuery(cusQuery);
        resetRequestList();
        setCustomerPage(1);
        setCustomersList([]);
        getCustomersOfPendingProcessingOrders({ page: 1 });
      } else {
        if (showCusAdvanceForm === false) {
          AdvanceSearch();
        }
      }
    }, 500);
    return () => clearTimeout(t);
  }, [cusQuery, activeFilter, isAdvanceMode, showCusAdvanceForm]);

  return (
    <div className="d-flex justify-content-between gap-3 w-100 ">
      <ShipmentCusSidebarLayout
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        navItems={navItems}
        cusQuery={cusQuery}
        setCusQuery={setCusQuery}
        setSelectedCustomer={onCustomerSelect}
        selectedCustomer={selectedCustomer}
        showCusAdvanceForm={showCusAdvanceForm}
        setShowCusAdvanceForm={setShowCusAdvanceForm}
        customersList={customersList}
        totalCustomers={totalCustomers}
        filterTabCounts={filterTabCounts}
        isAdvanceMode={isAdvanceMode}
        setIsAdvanceMode={setIsAdvanceMode}
        resetRequestList={resetRequestList}
      />
      <div className="customer-list-popup flex-grow-1">
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h4 className="text-nowrap">Customers by WO Status</h4>
          <div className="d-flex align-items-center gap-2">
            <span className="text-nowrap">
              Page: {customerPage} / {customerPages}
            </span>
            <span className="text-nowrap">Total: {totalCustomers}</span>
            <span className="text-nowrap">Limit: </span>
            <select
              value={customerLimit}
              className="form-select form-select-sm"
              onChange={(e) => {
                setCustomerLimit(Number(e.target.value));
                getCustomersOfPendingProcessingOrders({
                  // page: 1,
                  limit: Number(e.target.value),
                });
              }}
              style={{ width: 90 }}
            >
              {[5, 10, 20, 50].map((lim) => (
                <option key={lim} value={lim}>
                  {lim}
                </option>
              ))}
            </select>
            <span className="text-nowrap">
              Showing {customersList.length} of {totalCustomers} results
            </span>
          </div>
        </div>
        {customerLoading && <Spinner />}

        <div
          className="table-responsive"
          style={{
            height: "77vh",
            overflowY: "auto",
          }}
        >
          <table className="table table-sm table-hover mb-2">
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
              className="table-secondary"
            >
              <tr>
                <th style={{ width: "150px" }}>C-ID</th>
                <th>Name</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customersList.map((item) => {
                const isSelected =
                  selectedCustomer === item["customer.customerId"];
                return (
                  <tr
                    key={item.customerId}
                    className={isSelected ? "table-active" : ""}
                    style={{ cursor: "pointer" }}
                  >
                    <td
                      className="align-middle py-2 d-flex align-items-center gap-1"
                      style={{ width: "150px" }}
                    >
                      C-{item["customer.customerId"]}
                    </td>
                    <td className="align-middle py-1">
                      {(user?.permissions?.includes("work_orders_add") ||
                        user?.permissions?.includes("work_orders_update") ||
                        user?.permissions?.includes("work_orders_delete") ||
                        user?.permissions?.includes("work_orders_dataEntry") ||
                        user?.roles === "admin") && (
                        <span
                          className="btn btn-sm btn-info px-1 py-0 me-1"
                          onClick={() =>
                            handleViewCustomer(item["customer.customerId"])
                          }
                        >
                          View
                        </span>
                      )}

                      {truncateString(item?.customerName || "-", 50)}
                    </td>
                    <td className="align-middle py-1 text-end">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => onCustomerSelect(item)}
                      >
                        Select
                      </button>
                      {/* <AddProductsToShipment
                        shipment={shipment}
                        activeFilter={activeFilter}
                        selectedCustomer={item}
                        refreshCustomers={() => {
                          setCustomerPage(1);
                          getCustomersOfPendingProcessingOrders({ page: 1 });
                        }}
                      /> */}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalCustomers > customersList.length && (
            <div className="p-2 text-center bg-light">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleLoadMoreCustomers}
                disabled={customerLoading}
              >
                Load More
              </button>
            </div>
          )}
        </div>

        <Modal
          size="xl"
          show={showWOModal}
          onHide={() => setShowWOModals(false)}
          contentClassName="shadow-sm rounded-3"
        >
          <Modal.Body>
            {selectedCustomer && (
              <ViewWoByCus
                selectedCustomer={selectedCustomer}
                activeFilter={activeFilter}
                shipment={shipment}
              />
            )}
          </Modal.Body>
        </Modal>
        {/* Advance Form Modal */}
        <Modal
          size="xl"
          show={showCusAdvanceForm}
          onHide={() => setShowCusAdvanceForm(false)}
          contentClassName="shadow-sm rounded-3"
        >
          <Modal.Body>
            <ShipCusAdvanceFormLayout
              woStatus={activeFilter}
              showModal={setShowCusAdvanceForm}
              setSearchResult={setCustomersList}
              setFilterTabCounts={setFilterTabCounts}
              requestList={requestList}
              setRequestList={setRequestList}
              isAdvanceMode={isAdvanceMode}
              setIsAdvanceMode={setIsAdvanceMode}
            />
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}
